<template>
  <div
    ref="rootEl"
    class="embla relative"
    :style="{ '--embla-slide-size': `${slideSize}%` }"
    :tabindex="keyboard ? 0 : undefined"
    :aria-keyshortcuts="keyboard ? 'ArrowLeft ArrowRight' : undefined"
    @keydown="onKeydown"
    @pointerdown.capture="onEagerInteract"
  >
    <div
      :ref="setViewportRef"
      class="embla__viewport overflow-hidden"
    >
      <div
        :id="resolvedCarouselId"
        class="embla__container flex touch-pan-y"
      >
        <slot />
      </div>
    </div>

    <div
      ref="liveRegionRef"
      class="embla__live-region"
      aria-live="polite"
      aria-atomic="true"
    />

    <template v-if="showArrows">
      <button
        ref="prevButtonRef"
        type="button"
        class="embla__button embla__button--prev"
        :aria-label="resolvedA11y.previousButtonAriaLabel"
        :aria-disabled="!canScrollPrev"
        @click.stop.prevent="scrollPrev"
      >
        <span class="sr-only">{{ resolvedA11y.previousButtonAriaLabel }}</span>
        <slot name="prev">
          <span aria-hidden="true">&lsaquo;</span>
        </slot>
      </button>
      <button
        ref="nextButtonRef"
        type="button"
        class="embla__button embla__button--next"
        :aria-label="resolvedA11y.nextButtonAriaLabel"
        :aria-disabled="!canScrollNext"
        @click.stop.prevent="scrollNext"
      >
        <span class="sr-only">{{ resolvedA11y.nextButtonAriaLabel }}</span>
        <slot name="next">
          <span aria-hidden="true">&rsaquo;</span>
        </slot>
      </button>
    </template>

    <div
      v-if="$slots.dots"
      class="embla__dots"
    >
      <slot name="dots" />
    </div>
  </div>
</template>

<script setup lang="ts">
import useEmblaCarousel from 'embla-carousel-vue'
import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from 'embla-carousel'
import Ssr from 'embla-carousel-ssr'
import type { SsrOptionsType } from 'embla-carousel-ssr'
import Accessibility from 'embla-carousel-accessibility'
import type { AccessibilityOptionsType } from 'embla-carousel-accessibility'

type SsrBreakpoints = NonNullable<SsrOptionsType['breakpoints']>

interface Props {
  options?: EmblaOptionsType
  /** Percent width of each slide (must match CSS and SSR `slideSizes`). */
  slideSize?: number
  /** Slide count for SSR `slideSizes` (required with slot markup). */
  slideCount?: number
  /** Explicit SSR slide sizes in percent; overrides `slideSize`/`slideCount`. */
  slideSizes?: number[]
  /** Embla SSR breakpoints (media query → `slideSizes`). */
  ssrBreakpoints?: SsrBreakpoints
  showArrows?: boolean
  /** Unique container id for SSR styles; falls back to `useId()`. */
  carouselId?: string
  /** Enable accessibility or override plugin options. */
  accessibility?: boolean | AccessibilityOptionsType
  /**
   * Selector relative to a11y `rootNode` for the dot-buttons wrapper.
   * Buttons must be direct children of this wrapper (Embla a11y API).
   */
  dotsSelector?: string
  /**
   * Selector: pointerdown in a matching descendant cancels parent drag (nested carousels).
   */
  ignoreDragInside?: string
  /** SSR plugin and SSR head styles (default true). Modal carousels: false. */
  ssr?: boolean
  /** After client init, jump to this snap (wait until viewport width > 0). */
  initialSnap?: number
  /**
   * Make the carousel region focusable and operable via ArrowLeft/ArrowRight.
   * Use when dots are non-interactive indicators so keyboard users can still navigate.
   */
  keyboard?: boolean
  /**
   * Keep SSR markup/CSS, but delay Embla client init until near the viewport
   * (or first pointer interaction). Use for many below-fold instances.
   */
  deferUntilVisible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options: () => ({ align: 'start', loop: false }),
  slideSize: 100,
  slideCount: 0,
  slideSizes: undefined,
  ssrBreakpoints: undefined,
  showArrows: false,
  carouselId: undefined,
  accessibility: true,
  dotsSelector: '.embla__dots-inner',
  ignoreDragInside: undefined,
  ssr: true,
  initialSnap: 0,
  keyboard: false,
  deferUntilVisible: false
})

const emit = defineEmits<{
  select: [index: number]
  init: [api: EmblaCarouselType]
}>()

const autoId = useId().replace(/\W/g, '')
const resolvedCarouselId = computed(() => props.carouselId || `embla-${autoId}`)

const prevButtonRef = ref<HTMLElement | null>(null)
const nextButtonRef = ref<HTMLElement | null>(null)
const liveRegionRef = ref<HTMLElement | null>(null)

const a11yEnabled = computed(() => props.accessibility !== false)

const { translate: t } = useTranslations('UiCarousel')

const resolvedA11y = computed(() => {
  const defaults: AccessibilityOptionsType = {
    announceChanges: true,
    carouselAriaLabel: t.value('carousel_aria_label'),
    previousButtonAriaLabel: t.value('previous_button_aria_label'),
    nextButtonAriaLabel: t.value('next_button_aria_label'),
    rootNode: (emblaRoot) => emblaRoot.parentElement
  }

  if (typeof props.accessibility === 'object' && props.accessibility) {
    return { ...defaults, ...props.accessibility }
  }

  return defaults
})

const resolvedSlideSizes = computed(() => {
  if (props.slideSizes?.length) {
    return props.slideSizes
  }
  return Array.from({ length: Math.max(props.slideCount, 0) }, () => props.slideSize)
})

const emblaOptions = computed<EmblaOptionsType>(() => ({
  align: 'start',
  loop: false,
  ...props.options,
  breakpoints: {
    '(prefers-reduced-motion: reduce)': { duration: 0 },
    ...props.options?.breakpoints
  }
}))

const emblaPlugins = computed(() => {
  const plugins: EmblaPluginType[] = []

  if (props.ssr) {
    plugins.push(Ssr({
      slideSizes: resolvedSlideSizes.value,
      ...(props.ssrBreakpoints ? { breakpoints: props.ssrBreakpoints } : {})
    }))
  }

  if (a11yEnabled.value) {
    plugins.push(Accessibility(resolvedA11y.value))
  }

  return plugins
})

const [emblaRootRef, emblaApi, emblaServerApi] = useEmblaCarousel(emblaOptions, emblaPlugins)

/** Viewport node always present for SSR/DOM; assigned to Embla only when client init is allowed. */
const rootEl = ref<HTMLElement | null>(null)
const viewportEl = ref<HTMLElement | null>(null)
const allowClientInit = ref(!props.deferUntilVisible)

const setViewportRef = (el: unknown): void => {
  viewportEl.value = el instanceof HTMLElement ? el : null
}

const syncEmblaRoot = (): void => {
  if (allowClientInit.value && viewportEl.value) {
    if (emblaRootRef.value !== viewportEl.value) {
      emblaRootRef.value = viewportEl.value
    }
    return
  }
  if (emblaRootRef.value) {
    emblaRootRef.value = undefined
  }
}

watch([allowClientInit, viewportEl], syncEmblaRoot, { immediate: true })

const enableClientInit = (): void => {
  if (allowClientInit.value) {
    syncEmblaRoot()
    return
  }
  allowClientInit.value = true
  syncEmblaRoot()
}

/** First interaction before IO (e.g. nested carousel) still activates Embla. */
const onEagerInteract = (): void => {
  if (props.deferUntilVisible) {
    enableClientInit()
  }
}

const deferObserver = shallowRef<IntersectionObserver | null>(null)

const observeRootForDefer = (el: HTMLElement | null): void => {
  if (!deferObserver.value || !(el instanceof HTMLElement)) {
    return
  }
  deferObserver.value.observe(el)
}

onMounted(() => {
  if (!props.deferUntilVisible || allowClientInit.value) {
    return
  }
  if (typeof IntersectionObserver === 'undefined') {
    enableClientInit()
    return
  }

  deferObserver.value = new IntersectionObserver(
    (entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        enableClientInit()
        deferObserver.value?.disconnect()
        deferObserver.value = null
      }
    },
    // Start slightly before entering the viewport to avoid interaction lag / CLS in-view.
    { root: null, rootMargin: '200px 0px', threshold: 0 }
  )

  observeRootForDefer(rootEl.value)
})

watch(rootEl, (el, prev) => {
  if (!deferObserver.value) {
    return
  }
  if (prev instanceof HTMLElement) {
    deferObserver.value.unobserve(prev)
  }
  observeRootForDefer(el)
})

onBeforeUnmount(() => {
  deferObserver.value?.disconnect()
  deferObserver.value = null
})

const renderSsrStyles = computed(() => props.ssr && !emblaApi.value)

const ssrTransformCss = computed(() => {
  if (!props.ssr) {
    return ''
  }
  return emblaServerApi.plugins().ssr?.getStyles(`#${resolvedCarouselId.value}`, '.embla__slide') ?? ''
})

/** Breakpoint overrides only via `useHead`; default width comes synchronously from `--embla-slide-size`. */
const slideBreakpointCss = computed(() => {
  if (!props.ssrBreakpoints) {
    return ''
  }
  const id = resolvedCarouselId.value
  const lines: string[] = []
  for (const [query, breakpointOptions] of Object.entries(props.ssrBreakpoints)) {
    const sizes = breakpointOptions?.slideSizes
    if (!sizes?.length) {
      continue
    }
    lines.push(`@media ${query} { #${id} > .embla__slide { flex: 0 0 ${sizes[0]}%; } }`)
  }
  return lines.join('\n')
})

useHead(() => {
  const styles: { key: string, children: string }[] = []

  if (slideBreakpointCss.value) {
    styles.push({
      key: `embla-size-${resolvedCarouselId.value}`,
      children: slideBreakpointCss.value
    })
  }

  if (renderSsrStyles.value && ssrTransformCss.value) {
    styles.push({
      key: `embla-ssr-${resolvedCarouselId.value}`,
      children: ssrTransformCss.value
    })
  }

  return { style: styles }
})

const canScrollPrev = ref(false)
const canScrollNext = ref(false)
const isScrollable = computed(() => canScrollPrev.value || canScrollNext.value)

/** Tracks last applied `draggable` to avoid reInit loops. Starts as Embla default. */
let appliedDraggable: boolean | null = true

const updateScrollState = (): void => {
  const api = emblaApi.value
  if (!api) {
    canScrollPrev.value = false
    canScrollNext.value = false
    return
  }
  canScrollPrev.value = api.canGoToPrev()
  canScrollNext.value = api.canGoToNext()
}

const syncDraggable = (api: EmblaCarouselType): void => {
  const scrollable = api.canGoToPrev() || api.canGoToNext()
  const next = props.options?.draggable === false ? false : scrollable
  if (next === appliedDraggable) {
    return
  }
  appliedDraggable = next
  api.reInit({ draggable: next })
}

const onReinitOrResize = (): void => {
  const api = emblaApi.value
  if (!api) {
    return
  }
  updateScrollState()
  syncDraggable(api)
}

const onSelect = (): void => {
  const api = emblaApi.value
  if (!api) {
    return
  }
  updateScrollState()
  emit('select', api.selectedSnap())
}

const setupAccessibility = (api: EmblaCarouselType): void => {
  const a11y = api.plugins().accessibility
  if (!a11y) {
    return
  }

  if (liveRegionRef.value) {
    a11y.setupLiveRegion(liveRegionRef.value)
  }

  if (prevButtonRef.value && nextButtonRef.value) {
    a11y.setupPrevAndNextButtons(prevButtonRef.value, nextButtonRef.value)
  }

  if (props.dotsSelector) {
    a11y.setupDotButtons(props.dotsSelector)
  }
}

const scrollPrev = (): void => {
  if (!emblaApi.value && props.deferUntilVisible) {
    enableClientInit()
    void nextTick(() => {
      if (emblaApi.value?.canGoToPrev()) {
        emblaApi.value.goToPrev()
      }
      updateScrollState()
    })
    return
  }
  if (!canScrollPrev.value) {
    return
  }
  emblaApi.value?.goToPrev()
}

const scrollNext = (): void => {
  if (!emblaApi.value && props.deferUntilVisible) {
    enableClientInit()
    void nextTick(() => {
      if (emblaApi.value?.canGoToNext()) {
        emblaApi.value.goToNext()
      }
      updateScrollState()
    })
    return
  }
  if (!canScrollNext.value) {
    return
  }
  emblaApi.value?.goToNext()
}

const onKeydown = (event: KeyboardEvent): void => {
  if (!props.keyboard) {
    return
  }
  // Only react when the carousel region itself is focused, not inner controls/links.
  if (event.target !== event.currentTarget) {
    return
  }
  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    scrollPrev()
  }
  else if (event.key === 'ArrowRight') {
    event.preventDefault()
    scrollNext()
  }
}

const scrollTo = (index: number, instant = true): void => {
  if (!emblaApi.value && props.deferUntilVisible) {
    enableClientInit()
    void nextTick(() => {
      const api = emblaApi.value
      if (!api || api.selectedSnap() === index) {
        return
      }
      api.goTo(index, instant)
    })
    return
  }
  const api = emblaApi.value
  if (!api || api.selectedSnap() === index) {
    return
  }
  api.goTo(index, instant)
}

const getInitialSnapTarget = (): number => {
  return props.initialSnap ?? props.options?.startSnap ?? 0
}

const isSnapLayoutReady = (api: EmblaCarouselType): boolean => {
  const root = api.rootNode()
  if (root.clientWidth <= 0) {
    return false
  }
  const firstSlide = api.slideNodes()[0]
  if (!firstSlide || firstSlide.clientWidth <= 0) {
    return false
  }
  // Full-width slides must roughly match the viewport before goTo has displacement.
  if (props.slideSize >= 100) {
    return firstSlide.clientWidth >= root.clientWidth * 0.9
  }
  return true
}

const jumpToInitialSnap = (api: EmblaCarouselType): void => {
  const target = getInitialSnapTarget()
  if (!target) {
    return
  }
  api.reInit()
  if (api.selectedSnap() !== target) {
    api.goTo(target, true)
  }
}

const applyInitialSnapWhenReady = (api: EmblaCarouselType): (() => void) => {
  if (!getInitialSnapTarget()) {
    return () => {}
  }

  let done = false
  let rafId = 0
  const root = api.rootNode()
  const container = api.containerNode()

  const tryJump = (): boolean => {
    if (done || !isSnapLayoutReady(api)) {
      return false
    }
    done = true
    jumpToInitialSnap(api)
    return true
  }

  if (tryJump()) {
    return () => {}
  }

  const observer = new ResizeObserver(() => {
    if (tryJump()) {
      observer.disconnect()
      cancelAnimationFrame(rafId)
    }
  })
  observer.observe(root)
  if (container) {
    observer.observe(container)
  }

  // Teleport/modal paint: width can exist before flex layout settles.
  nextTick(() => {
    const run = (remaining: number) => {
      if (tryJump() || remaining <= 0) {
        if (done) {
          observer.disconnect()
        }
        return
      }
      rafId = requestAnimationFrame(() => run(remaining - 1))
    }
    run(2)
  })

  return () => {
    done = true
    observer.disconnect()
    cancelAnimationFrame(rafId)
  }
}

const onPointerDown = (_api: EmblaCarouselType, event: { detail: Event }): boolean => {
  if (!props.ignoreDragInside) {
    return true
  }
  const target = event.detail.target
  if (target instanceof Element && target.closest(props.ignoreDragInside)) {
    return false
  }
  return true
}

watch(emblaApi, (api, _prev, onCleanup) => {
  if (!api) {
    updateScrollState()
    return
  }

  nextTick(() => {
    setupAccessibility(api)
  })

  updateScrollState()
  syncDraggable(api)
  api.on('select', onSelect)
  api.on('reinit', onReinitOrResize)
  api.on('resize', onReinitOrResize)
  if (props.ignoreDragInside) {
    api.on('pointerdown', onPointerDown)
  }

  const stopInitialSnap = applyInitialSnapWhenReady(api)
  emit('init', api)

  onCleanup(() => {
    stopInitialSnap()
    appliedDraggable = true
    api.off('select', onSelect)
    api.off('reinit', onReinitOrResize)
    api.off('resize', onReinitOrResize)
    if (props.ignoreDragInside) {
      api.off('pointerdown', onPointerDown)
    }
  })
}, { immediate: true })

watch(
  () => [prevButtonRef.value, nextButtonRef.value, liveRegionRef.value, props.showArrows, props.dotsSelector],
  () => {
    const api = emblaApi.value
    if (api) {
      nextTick(() => setupAccessibility(api))
    }
  }
)

defineExpose({
  emblaApi,
  scrollPrev,
  scrollNext,
  scrollTo,
  canScrollPrev,
  canScrollNext,
  isScrollable
})
</script>

<style scoped>
.embla:focus-visible {
  outline: 3px solid #005fcc;
  outline-offset: 2px;
}

.embla__container {
  touch-action: pan-y pinch-zoom;
}

.embla__container > :deep(.embla__slide) {
  flex: 0 0 var(--embla-slide-size, 100%);
  min-width: 0;
}

.embla__live-region {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.embla__button {
  position: absolute;
  top: 50%;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  transform: translateY(-50%);
  border: 0;
  border-radius: 9999px;
  background: rgb(255 255 255 / 90%);
  color: inherit;
  cursor: pointer;
}

.embla__button[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

.embla__button--prev {
  left: 0.5rem;
}

.embla__button--next {
  right: 0.5rem;
}
</style>
